import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ShieldCheck, Users, Activity, ArrowLeft, Plus, Check, X,
  Trash2, PlusCircle, Ban, RefreshCw, Pencil, Save, Coins, AlertTriangle,
  Undo2, Mail, Download, Tag, Gift, Handshake, CreditCard, MoveRight,
  Search, ArrowUpDown, ScrollText,
} from "lucide-react";
import Header from "@/components/Header";
import { useAuth } from "@/context/AuthContext";
import {
  adminStats, adminListWhitelist, adminAddWhitelist, adminUpdateWhitelist,
  adminDeleteWhitelist, adminAddCredits, adminDeleteCreditNote, adminListRequests,
  adminApproveRequest, adminRejectRequest, adminAuditLog, adminLlmHealth,
  adminListCreditRequests, adminApproveCreditRequest, adminRejectCreditRequest,
  adminFailuresStats, adminManualRefund, adminListManualRefunds,
  adminListWaitlist, adminExportWaitlistCsvUrl, adminDeleteWaitlist,
  adminDeleteAuditEntry, adminClearAuditLog, adminDeleteAuditDay,
  adminDeleteManualRefund,
} from "@/lib/api";
import { getSessionToken } from "@/lib/auth";
import { FailedMasterings } from "@/components/admin/FailedMasterings";

function StatCard({ label, value, tone = "default", testid }) {
  const tones = {
    default: "border-white/10",
    green: "border-emerald-500/30 bg-emerald-500/5",
    red: "border-red-500/30 bg-red-500/5",
    amber: "border-amber-500/30 bg-amber-500/5",
  };
  return (
    <div
      className={`rounded-xl border ${tones[tone]} p-4 flex flex-col gap-1`}
      data-testid={testid}
    >
      <span className="text-[10px] tiny-caps text-[color:var(--fg-muted)]">
        {label}
      </span>
      <span className="text-2xl font-bold text-white font-display leading-none">
        {value}
      </span>
    </div>
  );
}

export default function AdminPage() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const { user, loading } = useAuth();

  const [stats, setStats] = useState(null);
  const [llmHealth, setLlmHealth] = useState(null);
  const [failures, setFailures] = useState(null);
  const [whitelist, setWhitelist] = useState([]);
  const [requests, setRequests] = useState([]);
  const [creditReqs, setCreditReqs] = useState([]);
  const [audit, setAudit] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addCreditsFor, setAddCreditsFor] = useState(null);
  const [editingEmail, setEditingEmail] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [refundList, setRefundList] = useState([]);
  const [refundEmail, setRefundEmail] = useState("");
  const [refundAmount, setRefundAmount] = useState(1);
  const [refundReason, setRefundReason] = useState("");
  const [refunding, setRefunding] = useState(false);
  const [waitlist, setWaitlist] = useState([]);
  const [waitlistTotal, setWaitlistTotal] = useState(0);
  // Tab navigation for admin sections (whitelist / waitlist / resellers /
  // free_trial / subscribers / refunds). Persist to localStorage so
  // refreshes keep the admin in the same segment.
  const [activeTab, setActiveTab] = useState(() => {
    try {
      const v = localStorage.getItem("spectraloom_admin_tab");
      return v || "whitelist";
    } catch {
      return "whitelist";
    }
  });
  useEffect(() => {
    try { localStorage.setItem("spectraloom_admin_tab", activeTab); } catch {}
  }, [activeTab]);
  // Track which row's Move menu is open (email string or null).
  const [moveMenuFor, setMoveMenuFor] = useState(null);
  // Search + sort state (shared across the 4 whitelist tabs + waitlist).
  const [searchQuery, setSearchQuery] = useState("");
  const [sortMode, setSortMode] = useState("date_desc"); // date_desc | name_asc | credits_desc

  const refresh = useCallback(async () => {
    // `Promise.allSettled` so a single failing endpoint (temporary 404 during
    // deploy propagation, CDN cache mismatch, etc.) doesn't wipe out the
    // whole panel. Each section renders what it has, missing sections just
    // fall back to their empty state.
    const results = await Promise.allSettled([
      adminStats(),
      adminListWhitelist(),
      adminListRequests(),
      adminListCreditRequests(),
      adminAuditLog(50),
      adminFailuresStats(24),
      adminListManualRefunds(20),
      adminListWaitlist(500),
      adminLlmHealth(),
    ]);
    const [s, wl, rq, cr, al, fs, rl, wt, lh] = results;
    if (s.status === "fulfilled") setStats(s.value);
    if (wl.status === "fulfilled") setWhitelist(wl.value.items || []);
    if (rq.status === "fulfilled") setRequests(rq.value.items || []);
    if (cr.status === "fulfilled") setCreditReqs(cr.value.items || []);
    if (al.status === "fulfilled") setAudit(al.value.items || []);
    if (fs.status === "fulfilled") setFailures(fs.value);
    if (rl.status === "fulfilled") setRefundList(rl.value.items || []);
    if (wt.status === "fulfilled") {
      setWaitlist(wt.value.items || []);
      setWaitlistTotal(wt.value.total || 0);
    }
    if (lh.status === "fulfilled") setLlmHealth(lh.value);
    // Surface ONLY the first failed call so the admin sees what's broken
    // without a flood of toasts. Rejected calls still let the rest render.
    const firstErr = results.find((r) => r.status === "rejected");
    if (firstErr) {
      const e = firstErr.reason;
      const detail = e?.response?.data?.detail || e?.message;
      // Skip transient 401/403 during login handshake · the redirect
      // effect below will handle non-admin bounce.
      const st = e?.response?.status;
      if (st !== 401 && st !== 403) {
        toast.error(t("admin.toast_error", { msg: detail }));
      }
    }
  }, [t]);

  const submitManualRefund = async () => {
    const email = refundEmail.trim().toLowerCase();
    const reason = refundReason.trim();
    const amount = Number(refundAmount) || 1;
    if (!email) {
      toast.error(t("admin.refund_toast_missing_email"));
      return;
    }
    if (reason.length < 3) {
      toast.error(t("admin.refund_toast_missing_reason"));
      return;
    }
    if (!window.confirm(t("admin.refund_confirm", { email, amount }))) return;
    setRefunding(true);
    try {
      const res = await adminManualRefund(email, amount, reason);
      toast.success(
        t("admin.refund_toast_ok", { amount: res.amount, email: res.email })
      );
      setRefundEmail("");
      setRefundAmount(1);
      setRefundReason("");
      await refresh();
    } catch (e) {
      toast.error(
        t("admin.toast_error", { msg: e.response?.data?.detail || e.message })
      );
    } finally {
      setRefunding(false);
    }
  };

  useEffect(() => {
    if (!loading && user?.is_admin) refresh();
  }, [loading, user, refresh]);

  const downloadWaitlistCsv = async () => {
    // The endpoint is admin-guarded, so we can't just `window.open` it in
    // cross-origin previews (the httpOnly session cookie isn't sent). We
    // fetch with the same interceptor headers the rest of the panel uses,
    // then trigger a client-side download via an object URL.
    try {
      const res = await fetch(adminExportWaitlistCsvUrl(), {
        method: "GET",
        credentials: "include",
        headers: {
          ...(getSessionToken()
            ? { Authorization: `Bearer ${getSessionToken()}` }
            : {}),
        },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 16).replace(/[:T]/g, "-");
      a.download = `spectraloom-waitlist-${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) {
      toast.error(t("admin.toast_error", { msg: e.message }));
    }
  };

  const handleDeleteWaitlist = async (email) => {
    if (!window.confirm(t("admin_waitlist.delete_confirm", { email }))) return;
    try {
      await adminDeleteWaitlist(email);
      toast.success(t("admin_waitlist.delete_ok", { email }));
      await refresh();
    } catch (e) {
      toast.error(
        t("admin.toast_error", {
          msg: e?.response?.data?.detail || e.message,
        })
      );
    }
  };

  // Redirect non-admin users away from /admin (side-effect, not during render).
  useEffect(() => {
    if (!loading && user && !user.is_admin) {
      nav("/", { replace: true });
    }
  }, [loading, user, nav]);

  if (loading) return null;
  if (!user?.is_admin) return null;

  const handleAdd = async (email, credits, notes) => {
    try {
      await adminAddWhitelist(email, credits, notes);
      toast.success(t("admin.toast_added"));
      setShowAddModal(false);
      refresh();
    } catch (e) {
      toast.error(t("admin.toast_error", { msg: e.response?.data?.detail || e.message }));
    }
  };

  const handlePatchCredits = async (email, newCredits) => {
    try {
      await adminUpdateWhitelist(email, { credit_limit: newCredits });
      toast.success(t("admin.toast_updated"));
      setEditingEmail(null);
      refresh();
    } catch (e) {
      toast.error(t("admin.toast_error", { msg: e.response?.data?.detail || e.message }));
    }
  };

  const handleRevoke = async (email) => {
    if (!window.confirm(t("admin.whitelist_revoke_confirm", { email }))) return;
    try {
      await adminUpdateWhitelist(email, { status: "revoked" });
      toast.success(t("admin.toast_updated"));
      refresh();
    } catch (e) {
      toast.error(t("admin.toast_error", { msg: e.response?.data?.detail || e.message }));
    }
  };

  const handleReactivate = async (email) => {
    try {
      await adminUpdateWhitelist(email, { status: "active" });
      toast.success(t("admin.toast_updated"));
      refresh();
    } catch (e) {
      toast.error(t("admin.toast_error", { msg: e.response?.data?.detail || e.message }));
    }
  };

  const handleDelete = async (email) => {
    if (!window.confirm(t("admin.whitelist_delete_confirm", { email }))) return;
    try {
      await adminDeleteWhitelist(email);
      toast.success(t("admin.toast_deleted"));
      refresh();
    } catch (e) {
      toast.error(t("admin.toast_error", { msg: e.response?.data?.detail || e.message }));
    }
  };

  const handleAddCredits = async (email, delta, note = "") => {
    try {
      await adminAddCredits(email, delta, note);
      toast.success(t("admin.toast_credits_added"));
      setAddCreditsFor(null);
      refresh();
    } catch (e) {
      toast.error(t("admin.toast_error", { msg: e.response?.data?.detail || e.message }));
    }
  };

  const handleDeleteCreditNote = async (email, noteId) => {
    try {
      await adminDeleteCreditNote(email, noteId);
      refresh();
    } catch (e) {
      toast.error(t("admin.toast_error", { msg: e.response?.data?.detail || e.message }));
    }
  };

  const handleDeleteAuditEntry = async (logId) => {
    try {
      await adminDeleteAuditEntry(logId);
      refresh();
    } catch (e) {
      toast.error(t("admin.toast_error", { msg: e.response?.data?.detail || e.message }));
    }
  };

  const handleClearAuditLog = async () => {
    if (!window.confirm(t("admin.audit_clear_confirm", { defaultValue: "Cancellare TUTTI i log delle azioni? Operazione irreversibile." }))) return;
    try {
      await adminClearAuditLog();
      toast.success(t("admin.audit_cleared", { defaultValue: "Log cancellato" }));
      refresh();
    } catch (e) {
      toast.error(t("admin.toast_error", { msg: e.response?.data?.detail || e.message }));
    }
  };

  const handleDeleteAuditDay = async (day) => {
    if (!window.confirm(t("admin.audit_day_confirm", { defaultValue: `Cancellare tutte le voci del ${day}?` }))) return;
    try {
      await adminDeleteAuditDay(day);
      refresh();
    } catch (e) {
      toast.error(t("admin.toast_error", { msg: e.response?.data?.detail || e.message }));
    }
  };

  const handleDeleteManualRefund = async (refundId) => {
    try {
      await adminDeleteManualRefund(refundId);
      refresh();
    } catch (e) {
      toast.error(t("admin.toast_error", { msg: e.response?.data?.detail || e.message }));
    }
  };

  // Move a whitelisted user to a different segment tab by updating the
  // `tag` field on the server. Passing "whitelist" (or "") strips the tag.
  const handleMoveTag = async (email, newTag) => {
    try {
      await adminUpdateWhitelist(email, { tag: newTag });
      toast.success(t("admin.toast_moved", { defaultValue: "Utente spostato" }));
      setMoveMenuFor(null);
      refresh();
    } catch (e) {
      toast.error(t("admin.toast_error", { msg: e.response?.data?.detail || e.message }));
    }
  };

  // Segment tabs → server tag values used to filter the whitelist array.
  // The "whitelist" tab is the MASTER list: it shows ALL authorized users
  // EXCEPT disposable_blocked ones (which get their own dedicated tab).
  // The other segment tabs filter to a specific tag only.
  const TAB_TO_TAG = {
    whitelist: null,        // null = no filter (except disposable_blocked)
    free_trial: "free_trial",
    resellers: "reseller",
    subscribers: "subscriber",
    blocked: "disposable_blocked",
  };
  const isWhitelistTab = activeTab in TAB_TO_TAG;
  const _baseWhitelist = !isWhitelistTab
    ? whitelist
    : TAB_TO_TAG[activeTab] === null
      ? whitelist.filter((w) => (w.tag || "").toLowerCase() !== "disposable_blocked")  // Master: hide blocked
      : whitelist.filter((w) => (w.tag || "").toLowerCase() === TAB_TO_TAG[activeTab]);

  // Search + sort applied AFTER segment filtering. Same query/mode is
  // reused on the Waitlist tab (filtered below via `filteredWaitlist`).
  const _q = searchQuery.trim().toLowerCase();
  const _matches = (u) => {
    if (!_q) return true;
    const name = (u.name || u.user_name || "").toLowerCase();
    const email = (u.email || "").toLowerCase();
    return name.includes(_q) || email.includes(_q);
  };
  const _sortFn = (a, b) => {
    if (sortMode === "name_asc") {
      return (a.name || a.email || "").localeCompare(b.name || b.email || "");
    }
    if (sortMode === "credits_desc") {
      return (b.credit_limit || 0) - (a.credit_limit || 0);
    }
    // date_desc (default)
    const da = a.created_at || "";
    const db_ = b.created_at || "";
    return db_.localeCompare(da);
  };
  const filteredWhitelist = _baseWhitelist.filter(_matches).slice().sort(_sortFn);
  const filteredWaitlist = waitlist.filter(_matches).slice().sort(_sortFn);
  const countByTag = {
    whitelist: whitelist.filter((w) => (w.tag || "") !== "disposable_blocked").length,  // Master: exclude blocked
    free_trial: whitelist.filter((w) => (w.tag || "") === "free_trial").length,
    resellers: whitelist.filter((w) => (w.tag || "") === "reseller").length,
    subscribers: whitelist.filter((w) => (w.tag || "") === "subscriber").length,
    blocked: whitelist.filter((w) => (w.tag || "") === "disposable_blocked").length,
  };

  const handleGrantCreditRequest = async (req) => {
    const val = window.prompt(
      t("admin.credit_requests_grant_prompt", {
        email: req.email,
        requested: req.requested_amount,
      }),
      String(req.requested_amount),
    );
    if (!val) return;
    const granted = Math.max(1, Math.min(99999, parseInt(val, 10) || req.requested_amount));
    try {
      await adminApproveCreditRequest(req.id, granted);
      toast.success(t("admin.toast_credits_added"));
      refresh();
    } catch (e) {
      toast.error(t("admin.toast_error", { msg: e.response?.data?.detail || e.message }));
    }
  };

  const handleRejectCreditRequest = async (req) => {
    const note = window.prompt(t("admin.credit_requests_reject_prompt"), "");
    try {
      await adminRejectCreditRequest(req.id, note || "");
      toast.success(t("admin.toast_rejected"));
      refresh();
    } catch (e) {
      toast.error(t("admin.toast_error", { msg: e.response?.data?.detail || e.message }));
    }
  };

  const handleApprove = async (email) => {
    const val = window.prompt(t("admin.requests_approve_prompt", { email }), "20");
    if (!val) return;
    const credits = Math.max(0, Math.min(99999, parseInt(val, 10) || 10));
    try {
      await adminApproveRequest(email, credits);
      toast.success(t("admin.toast_approved"));
      refresh();
    } catch (e) {
      toast.error(t("admin.toast_error", { msg: e.response?.data?.detail || e.message }));
    }
  };

  const handleReject = async (email) => {
    try {
      await adminRejectRequest(email);
      toast.success(t("admin.toast_rejected"));
      refresh();
    } catch (e) {
      toast.error(t("admin.toast_error", { msg: e.response?.data?.detail || e.message }));
    }
  };

  return (
    <div className="min-h-screen bg-[color:var(--bg-base)]" data-testid="admin-page">
      <Header />
      <main className="max-w-[1200px] mx-auto px-6 lg:px-10 py-8 md:py-12">
        <div className="flex items-center gap-3 mb-2">
          <button
            onClick={() => nav("/")}
            className="btn-ghost text-xs !py-1.5 !px-3 flex items-center gap-1.5"
            data-testid="admin-back-home"
          >
            <ArrowLeft size={12} /> {t("admin.back_home")}
          </button>
        </div>
        <div className="flex items-center gap-3 mb-6">
          <div className="w-11 h-11 rounded-lg bg-gradient-to-br from-emerald-500 to-blue-600 flex items-center justify-center shadow-lg">
            <ShieldCheck size={20} className="text-white" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-display font-bold text-white leading-none">
              {t("admin.page_title")}
            </h1>
            <p className="text-xs text-[color:var(--fg-muted)] mt-1">
              {t("admin.page_subtitle")}
            </p>
          </div>
          <button
            onClick={refresh}
            className="ml-auto btn-ghost text-xs !py-1.5 !px-3 flex items-center gap-1.5"
            data-testid="admin-refresh"
            title="Refresh"
          >
            <RefreshCw size={12} />
          </button>
        </div>

        {/* Tab navigation · 6 admin segments (main navigation) */}
        <div
          className="mb-8 border-b border-white/10 flex flex-wrap gap-1"
          data-testid="admin-tab-bar"
          role="tablist"
        >
          {[
            { id: "whitelist", label: t("admin.tab_whitelist", { defaultValue: "Whitelist" }), icon: ShieldCheck, count: countByTag.whitelist },
            { id: "waitlist", label: t("admin.tab_waitlist", { defaultValue: "Waitlist" }), icon: Mail, count: waitlistTotal },
            { id: "resellers", label: t("admin.tab_resellers", { defaultValue: "Resellers" }), icon: Handshake, count: countByTag.resellers },
            { id: "free_trial", label: t("admin.tab_free_trial", { defaultValue: "Free Trial" }), icon: Gift, count: countByTag.free_trial },
            { id: "subscribers", label: t("admin.tab_subscribers", { defaultValue: "Subscribers" }), icon: CreditCard, count: countByTag.subscribers },
            { id: "blocked", label: t("admin.tab_blocked", { defaultValue: "Blocked" }), icon: Ban, count: countByTag.blocked },
            { id: "refunds", label: t("admin.tab_refunds", { defaultValue: "Refunds" }), icon: Undo2, count: refundList.length },
            { id: "log", label: t("admin.tab_log", { defaultValue: "Log" }), icon: ScrollText, count: audit.length },
          ].map((tab) => {
            const Icon = tab.icon;
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setActiveTab(tab.id)}
                className={`inline-flex items-center gap-1.5 px-3 py-2 -mb-px text-xs border-b-2 transition-colors ${
                  active
                    ? "border-emerald-400 text-emerald-200"
                    : "border-transparent text-[color:var(--fg-muted)] hover:text-white hover:border-white/20"
                }`}
                data-testid={`admin-tab-${tab.id}`}
              >
                <Icon size={13} />
                <span className="font-medium">{tab.label}</span>
                {tab.count > 0 && (
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${
                    active
                      ? "bg-emerald-500/15 text-emerald-200 border-emerald-500/40"
                      : "bg-white/5 text-[color:var(--fg-muted)] border-white/10"
                  }`}>
                    {tab.count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Whitelist tab · full dashboard (stats + failures + pending requests + whitelist table + audit log) */}
        {activeTab === "whitelist" && (
        <>
        {/* LLM Health badge · live status of the Emergent LLM Key */}
        {llmHealth && (
          <div
            className={`rounded-xl border p-4 mb-6 flex items-center justify-between gap-4 flex-wrap ${
              llmHealth.status === "degraded"
                ? "border-red-500/40 bg-red-500/10"
                : llmHealth.status === "warning"
                ? "border-amber-500/40 bg-amber-500/10"
                : "border-emerald-500/30 bg-emerald-500/5"
            }`}
            data-testid="llm-health-card"
          >
            <div className="flex items-center gap-3">
              <span
                className={`w-2.5 h-2.5 rounded-full ${
                  llmHealth.status === "degraded"
                    ? "bg-red-500 animate-pulse"
                    : llmHealth.status === "warning"
                    ? "bg-amber-500"
                    : "bg-emerald-500"
                }`}
              />
              <div className="flex flex-col">
                <span className="text-[10px] tiny-caps text-[color:var(--fg-muted)]">
                  Emergent LLM Key
                </span>
                <span className="text-sm font-semibold text-white">
                  {llmHealth.status === "degraded"
                    ? "Circuit breaker attivo · analisi/chat bloccate"
                    : llmHealth.status === "warning"
                    ? "Errori recenti (non credit)"
                    : "Operativa"}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-4 text-xs text-white/70">
              <span data-testid="llm-health-credit-24h">
                Errori credit 24h:{" "}
                <b className="text-white">{llmHealth.credit_failures_24h}</b>
              </span>
              <span data-testid="llm-health-other-24h">
                Altri errori 24h:{" "}
                <b className="text-white">{llmHealth.other_failures_24h}</b>
              </span>
              {llmHealth.alert_sent_recently && (
                <span className="text-amber-300">📧 Alert inviato ≤6h fa</span>
              )}
              <a
                href="https://emergent.sh/profile"
                target="_blank"
                rel="noopener noreferrer"
                className="underline text-white hover:text-emerald-300"
              >
                Ricarica →
              </a>
            </div>
          </div>
        )}

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8">
            <StatCard label={t("admin.stats_total")} value={stats.total} testid="stat-total" />
            <StatCard label={t("admin.stats_active")} value={stats.active} tone="green" testid="stat-active" />
            <StatCard label={t("admin.stats_revoked")} value={stats.revoked} tone="red" testid="stat-revoked" />
            <StatCard label={t("admin.stats_pending")} value={stats.pending_requests} tone="amber" testid="stat-pending" />
            <StatCard label={t("admin.stats_analyses")} value={stats.analyses_total ?? stats.analyses_this_month ?? 0} testid="stat-analyses" />
          </div>
        )}

        {/* Failures 24h · auto-refunded credit incidents */}
        {failures && (
          <section
            className="mb-10 rounded-xl border border-white/10 bg-white/[0.02] p-5"
            data-testid="section-failures"
          >
            <div className="flex items-center gap-2 mb-4">
              <AlertTriangle size={16} className="text-amber-400" />
              <h2 className="text-base font-bold text-white">
                {t("admin.failures_title", { hours: failures.window_hours })}
              </h2>
              {failures.total > 0 && (
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-500/20 text-red-300 border border-red-500/30 ml-1">
                  {failures.total}
                </span>
              )}
            </div>

            {failures.total === 0 ? (
              !failures.failed_masterings?.length && (
                <p className="text-xs text-[color:var(--fg-muted)] italic" data-testid="failures-empty">
                  {t("admin.failures_empty", { hours: failures.window_hours })}
                </p>
              )
            ) : (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
                  <StatCard
                    label={t("admin.failures_total")}
                    value={failures.total}
                    tone="red"
                    testid="failures-total"
                  />
                  <StatCard
                    label={t("admin.failures_reason_server_restart")}
                    value={failures.by_reason.server_restart || 0}
                    tone="amber"
                    testid="failures-server-restart"
                  />
                  <StatCard
                    label={t("admin.failures_reason_job_failed")}
                    value={failures.by_reason.job_failed || 0}
                    tone="amber"
                    testid="failures-job-failed"
                  />
                  <StatCard
                    label={t("admin.failures_reason_other")}
                    value={Object.entries(failures.by_reason)
                      .filter(([k]) => k !== "server_restart" && k !== "job_failed")
                      .reduce((a, [, v]) => a + v, 0)}
                    testid="failures-other"
                  />
                </div>

                <div className="text-[10px] tiny-caps text-[color:var(--fg-muted)] mb-2">
                  {t("admin.failures_latest")}
                </div>
                <div className="rounded-lg border border-white/10 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-white/5 text-[10px] tiny-caps text-[color:var(--fg-muted)]">
                      <tr>
                        <th className="text-left px-4 py-2">Email</th>
                        <th className="text-left px-4 py-2">{t("admin.failures_file")}</th>
                        <th className="text-left px-4 py-2">{t("admin.failures_reason")}</th>
                        <th className="text-right px-4 py-2">{t("admin.failures_when")}</th>
                      </tr>
                    </thead>
                    <tbody data-testid="failures-table">
                      {failures.latest.map((row, idx) => (
                        <tr
                          key={`${row.analysis_id}-${idx}`}
                          className="border-t border-white/5"
                          data-testid={`failure-row-${idx}`}
                        >
                          <td className="px-4 py-2 text-white truncate max-w-[240px]">
                            {row.email || "·"}
                          </td>
                          <td className="px-4 py-2 text-[color:var(--fg-secondary)] truncate max-w-[220px]">
                            {row.filename || "·"}
                          </td>
                          <td className="px-4 py-2">
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${
                                row.reason === "server_restart"
                                  ? "bg-amber-500/10 text-amber-300 border-amber-500/30"
                                  : "bg-red-500/10 text-red-300 border-red-500/25"
                              }`}
                            >
                              {t(`admin.failures_reason_${row.reason}`, {
                                defaultValue: row.reason || "unknown",
                              })}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-right text-xs text-[color:var(--fg-secondary)]">
                            {row.created_at
                              ? new Date(row.created_at).toLocaleString()
                              : "·"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
            <FailedMasterings items={failures.failed_masterings} />
          </section>
        )}

        {/* Pending requests */}
        <section className="mb-10" data-testid="section-requests">
          <h2 className="text-base font-bold text-white mb-3 flex items-center gap-2">
            <Users size={16} /> {t("admin.requests_title")}
            {requests.length > 0 && (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/30">
                {requests.length}
              </span>
            )}
          </h2>
          {requests.length === 0 ? (
            <p className="text-xs text-[color:var(--fg-muted)] italic">{t("admin.requests_empty")}</p>
          ) : (
            <>
              {/* Mobile card view (< md) */}
              <div className="md:hidden space-y-3">
                {requests.map((r) => (
                  <div
                    key={r.email}
                    className="rounded-xl border border-white/10 bg-white/[0.02] p-4"
                    data-testid={`request-mobile-${r.email}`}
                  >
                    <div className="flex items-center gap-3 mb-3">
                      {r.picture ? (
                        <img src={r.picture} alt="" className="w-10 h-10 rounded-full flex-shrink-0" referrerPolicy="no-referrer" />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-white/10 flex-shrink-0" />
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="text-white text-sm font-medium truncate">{r.name || r.email}</div>
                        <div className="text-[11px] text-[color:var(--fg-muted)] truncate">{r.email}</div>
                      </div>
                    </div>
                    {r.user_message && (
                      <div className="mb-3 text-[12px] text-[color:var(--fg-secondary)] italic px-3 py-2 rounded-md bg-emerald-500/5 border-l-2 border-emerald-500/50">
                        <div className="text-[color:var(--fg-muted)] not-italic mb-1 text-[10px] tiny-caps">
                          {r.user_message_subject || t("admin.requests_user_message")}
                        </div>
                        <div className="whitespace-pre-wrap">"{r.user_message}"</div>
                      </div>
                    )}
                    <div className="flex items-center justify-between text-[11px] text-[color:var(--fg-muted)] mb-3">
                      <span>{new Date(r.last_attempt_at).toLocaleString()}</span>
                      <span>{r.attempts_count} {t("admin.requests_attempts").toLowerCase()}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        onClick={() => handleApprove(r.email)}
                        className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 text-sm rounded-lg bg-emerald-500/15 active:bg-emerald-500/30 hover:bg-emerald-500/25 text-emerald-200 border border-emerald-500/40 font-medium min-h-[44px]"
                        data-testid={`approve-mobile-${r.email}`}
                      >
                        <Check size={14} /> {t("admin.requests_approve")}
                      </button>
                      <button
                        onClick={() => handleReject(r.email)}
                        className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 text-sm rounded-lg bg-red-500/15 active:bg-red-500/30 hover:bg-red-500/25 text-red-200 border border-red-500/40 font-medium min-h-[44px]"
                        data-testid={`reject-mobile-${r.email}`}
                      >
                        <X size={14} /> {t("admin.requests_reject")}
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop table (>= md) */}
              <div className="hidden md:block rounded-xl border border-white/10 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-white/5 text-[10px] tiny-caps text-[color:var(--fg-muted)]">
                  <tr>
                    <th className="text-left px-4 py-2">Email</th>
                    <th className="text-left px-4 py-2">{t("admin.requests_last_attempt")}</th>
                    <th className="text-left px-4 py-2">{t("admin.requests_attempts")}</th>
                    <th className="text-right px-4 py-2">{t("admin.whitelist_actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {requests.map((r) => (
                    <tr key={r.email} className="border-t border-white/5" data-testid={`request-${r.email}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {r.picture ? (
                            <img src={r.picture} alt="" className="w-6 h-6 rounded-full" referrerPolicy="no-referrer" />
                          ) : (
                            <div className="w-6 h-6 rounded-full bg-white/10" />
                          )}
                          <div>
                            <div className="text-white text-xs">{r.name || r.email}</div>
                            <div className="text-[11px] text-[color:var(--fg-muted)]">{r.email}</div>
                          </div>
                        </div>
                        {r.user_message && (
                          <div
                            className="mt-2 text-[11px] text-[color:var(--fg-secondary)] italic px-2 py-1.5 rounded bg-emerald-500/5 border-l-2 border-emerald-500/50 max-w-md"
                            data-testid={`request-msg-${r.email}`}
                          >
                            <div className="text-[color:var(--fg-muted)] not-italic mb-0.5">
                              {r.user_message_subject || t("admin.requests_user_message")}
                            </div>
                            <div className="whitespace-pre-wrap">"{r.user_message}"</div>
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-[color:var(--fg-secondary)]">
                        {new Date(r.last_attempt_at).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-xs">{r.attempts_count}</td>
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex gap-2">
                          <button
                            onClick={() => handleApprove(r.email)}
                            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-md bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-300 border border-emerald-500/30"
                            data-testid={`approve-${r.email}`}
                          >
                            <Check size={12} /> {t("admin.requests_approve")}
                          </button>
                          <button
                            onClick={() => handleReject(r.email)}
                            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-md bg-red-500/10 hover:bg-red-500/20 text-red-300 border border-red-500/30"
                            data-testid={`reject-${r.email}`}
                          >
                            <X size={12} /> {t("admin.requests_reject")}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </>
          )}
        </section>

        {/* Credit top-up requests */}
        <section className="mb-10" data-testid="section-credit-requests">
          <h2 className="text-base font-bold text-white mb-3 flex items-center gap-2">
            <Coins size={16} className="text-amber-300" /> {t("admin.credit_requests_title")}
            {creditReqs.length > 0 && (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/30">
                {creditReqs.length}
              </span>
            )}
          </h2>
          {creditReqs.length === 0 ? (
            <p className="text-xs text-[color:var(--fg-muted)] italic">
              {t("admin.credit_requests_empty")}
            </p>
          ) : (
            <>
              {/* Mobile card view */}
              <div className="md:hidden space-y-3">
                {creditReqs.map((r) => (
                  <div
                    key={r.id}
                    className="rounded-xl border border-white/10 bg-white/[0.02] p-4"
                    data-testid={`credit-req-mobile-${r.email}`}
                  >
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div className="min-w-0 flex-1">
                        <div className="text-white text-sm font-medium truncate">{r.user_name || r.email}</div>
                        <div className="text-[11px] text-[color:var(--fg-muted)] truncate">{r.email}</div>
                        <div className="text-[10px] text-[color:var(--fg-muted)] mt-0.5">
                          {new Date(r.created_at).toLocaleString()}
                        </div>
                      </div>
                      <span className="inline-block px-2.5 py-1 rounded-md bg-amber-500/15 text-amber-300 text-sm border border-amber-500/40 tabular-nums font-semibold flex-shrink-0">
                        +{r.requested_amount}
                      </span>
                    </div>
                    {r.message && (
                      <div className="mb-3 text-[12px] text-[color:var(--fg-secondary)] italic px-3 py-2 rounded-md bg-white/[0.02] border-l-2 border-white/10 whitespace-pre-wrap break-words">
                        "{r.message}"
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        onClick={() => handleGrantCreditRequest(r)}
                        className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 text-sm rounded-lg bg-emerald-500/15 active:bg-emerald-500/30 hover:bg-emerald-500/25 text-emerald-200 border border-emerald-500/40 font-medium min-h-[44px]"
                        data-testid={`grant-credit-mobile-${r.email}`}
                      >
                        <Check size={14} /> {t("admin.credit_requests_grant")}
                      </button>
                      <button
                        onClick={() => handleRejectCreditRequest(r)}
                        className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 text-sm rounded-lg bg-red-500/15 active:bg-red-500/30 hover:bg-red-500/25 text-red-200 border border-red-500/40 font-medium min-h-[44px]"
                        data-testid={`reject-credit-mobile-${r.email}`}
                      >
                        <X size={14} /> {t("admin.credit_requests_reject")}
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop table */}
              <div className="hidden md:block rounded-xl border border-white/10 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-white/5 text-[10px] tiny-caps text-[color:var(--fg-muted)]">
                  <tr>
                    <th className="text-left px-4 py-2">{t("admin.whitelist_email")}</th>
                    <th className="text-left px-4 py-2">{t("admin.credit_requests_requested")}</th>
                    <th className="text-left px-4 py-2">{t("admin.credit_requests_message")}</th>
                    <th className="text-right px-4 py-2">{t("admin.whitelist_actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {creditReqs.map((r) => (
                    <tr
                      key={r.id}
                      className="border-t border-white/5"
                      data-testid={`credit-req-${r.email}`}
                    >
                      <td className="px-4 py-3">
                        <div className="text-white text-xs">{r.user_name || r.email}</div>
                        <div className="text-[11px] text-[color:var(--fg-muted)]">{r.email}</div>
                        <div className="text-[10px] text-[color:var(--fg-muted)] mt-0.5">
                          {new Date(r.created_at).toLocaleString()}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-block px-2 py-0.5 rounded-md bg-amber-500/10 text-amber-300 text-xs border border-amber-500/30 tabular-nums">
                          +{r.requested_amount}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-[color:var(--fg-secondary)] max-w-md truncate">
                        {r.message || <span className="italic text-[color:var(--fg-muted)]">·</span>}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex gap-2">
                          <button
                            onClick={() => handleGrantCreditRequest(r)}
                            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-md bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-300 border border-emerald-500/30"
                            data-testid={`grant-credit-${r.email}`}
                          >
                            <Check size={12} /> {t("admin.credit_requests_grant")}
                          </button>
                          <button
                            onClick={() => handleRejectCreditRequest(r)}
                            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-md bg-red-500/10 hover:bg-red-500/20 text-red-300 border border-red-500/30"
                            data-testid={`reject-credit-${r.email}`}
                          >
                            <X size={12} /> {t("admin.credit_requests_reject")}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </>
          )}
        </section>
        </>
        )}

        {/* Whitelist · filtered by activeTab (whitelist / free_trial / resellers / subscribers) */}
        {isWhitelistTab && (
        <section className="mb-10" data-testid={`section-${activeTab}`}>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base font-bold text-white flex items-center gap-2">
              <ShieldCheck size={16} /> {t(`admin.section_title_${activeTab}`, {
                defaultValue: t("admin.whitelist_title"),
              })}
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 text-[color:var(--fg-muted)] border border-white/10">
                {filteredWhitelist.length}
              </span>
            </h2>
            <button
              onClick={() => setShowAddModal(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-white text-xs border border-white/10"
              data-testid="whitelist-add-btn"
            >
              <Plus size={12} /> {t("admin.whitelist_add")}
            </button>
          </div>
          {/* Search + Sort bar */}
          <div className="mb-3 flex flex-col md:flex-row gap-2">
            <div className="flex-1 relative">
              <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--fg-muted)] pointer-events-none" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t("admin.search_placeholder", { defaultValue: "Cerca per nome o email…" })}
                className="w-full bg-white/5 border border-white/10 rounded-lg pl-8 pr-3 py-2 text-white text-sm placeholder:text-[color:var(--fg-muted)]"
                data-testid="admin-search-input"
              />
            </div>
            <div className="relative flex items-center gap-1.5">
              <ArrowUpDown size={12} className="text-[color:var(--fg-muted)]" />
              <select
                value={sortMode}
                onChange={(e) => setSortMode(e.target.value)}
                className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
                data-testid="admin-sort-select"
              >
                <option value="date_desc">{t("admin.sort_date", { defaultValue: "Data iscrizione ↓" })}</option>
                <option value="name_asc">{t("admin.sort_name", { defaultValue: "Nome A-Z" })}</option>
                <option value="credits_desc">{t("admin.sort_credits", { defaultValue: "Crediti ↓" })}</option>
              </select>
            </div>
          </div>
          {filteredWhitelist.length === 0 ? (
            <p className="text-xs text-[color:var(--fg-muted)] italic">
              {t(`admin.section_empty_${activeTab}`, {
                defaultValue: t("admin.whitelist_empty"),
              })}
            </p>
          ) : (
            <>
              {/* Mobile card view */}
              <div className="md:hidden space-y-3">
                {filteredWhitelist.map((it) => {
                  const isEditing = editingEmail === it.email;
                  return (
                    <div
                      key={it.email}
                      className="rounded-xl border border-white/10 bg-white/[0.02] p-4"
                      data-testid={`wl-mobile-${it.email}`}
                    >
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <div className="min-w-0 flex-1">
                          <div className="text-white text-sm font-medium truncate flex items-center gap-1.5">
                            {it.email}
                            {it.tag === "free_trial" && (
                              <span
                                className="text-[9px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/40 flex-shrink-0 tracking-wide"
                                title={t("admin.free_trial_hint")}
                              >
                                FREE TRIAL
                              </span>
                            )}
                          </div>
                          {it.name && (
                            <div className="text-[11px] text-[color:var(--fg-secondary)] mt-0.5 truncate">{it.name}</div>
                          )}
                          {it.created_at && (
                            <div className="text-[10px] text-[color:var(--fg-muted)] mt-0.5 tabular-nums">
                              {t("admin.whitelist_join_date", { defaultValue: "Iscritto il" })}: {new Date(it.created_at).toLocaleDateString()}
                            </div>
                          )}
                          {it.notes && (
                            <div className="text-[11px] text-[color:var(--fg-muted)] mt-0.5">{it.notes}</div>
                          )}
                          {Array.isArray(it.credit_notes) && it.credit_notes.length > 0 && (
                            <div className="mt-1.5 flex flex-wrap gap-1" data-testid={`credit-notes-mobile-${it.email}`}>
                              {it.credit_notes.slice().reverse().map((n) => (
                                <div
                                  key={n.id}
                                  className="inline-flex items-center gap-1.5 pl-2 pr-1 py-0.5 rounded bg-amber-500/10 border border-amber-500/25 text-[10px] text-amber-200"
                                >
                                  <PlusCircle size={9} className="text-amber-300" />
                                  <span className="tabular-nums font-semibold">+{n.delta}</span>
                                  {n.note && <span className="text-amber-100/80 italic">· {n.note}</span>}
                                  <span className="text-amber-100/50">· {new Date(n.created_at).toLocaleDateString()}</span>
                                  <button
                                    onClick={() => handleDeleteCreditNote(it.email, n.id)}
                                    className="ml-0.5 p-0.5 rounded hover:bg-red-500/25 text-amber-100/60 hover:text-red-300"
                                    title={t("admin.credit_note_delete", { defaultValue: "Rimuovi promemoria" })}
                                    data-testid={`credit-note-delete-mobile-${n.id}`}
                                  >
                                    <X size={10} />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        <span
                          className={`text-[10px] px-2 py-0.5 rounded-full border flex-shrink-0 ${
                            it.status === "active"
                              ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/30"
                              : "bg-red-500/10 text-red-300 border-red-500/30"
                          }`}
                        >
                          {it.status === "active"
                            ? t("admin.whitelist_status_active")
                            : t("admin.whitelist_status_revoked")}
                        </span>
                      </div>
                      <div className="text-xs mb-3 flex items-center gap-2">
                        <span className="text-[color:var(--fg-muted)] tiny-caps text-[10px]">{t("admin.whitelist_usage")}:</span>
                        {isEditing ? (
                          <input
                            type="number"
                            min={0}
                            max={99999}
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            className="w-24 bg-white/10 border border-white/20 rounded px-2 py-1 text-white text-sm"
                            data-testid={`edit-input-mobile-${it.email}`}
                          />
                        ) : (
                          <span>
                            <strong className="text-white">{it.credits_used}</strong>
                            <span className="text-[color:var(--fg-muted)]"> / {it.credit_limit === 99999 ? "∞" : it.credit_limit}</span>
                          </span>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        {isEditing ? (
                          <>
                            <button
                              onClick={() => handlePatchCredits(it.email, parseInt(editValue, 10) || 0)}
                              className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 text-sm rounded-lg bg-emerald-500/15 active:bg-emerald-500/30 text-emerald-200 border border-emerald-500/40 font-medium min-h-[44px]"
                              data-testid={`save-mobile-${it.email}`}
                            >
                              <Save size={14} /> {t("admin.whitelist_save") || "Salva"}
                            </button>
                            <button
                              onClick={() => setEditingEmail(null)}
                              className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 text-sm rounded-lg bg-white/5 active:bg-white/15 text-[color:var(--fg-secondary)] border border-white/10 font-medium min-h-[44px]"
                            >
                              <X size={14} /> {t("admin.whitelist_cancel") || "Annulla"}
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={() => {
                                setEditingEmail(it.email);
                                setEditValue(String(it.credit_limit));
                              }}
                              className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 text-sm rounded-lg bg-white/5 active:bg-white/15 text-white border border-white/10 font-medium min-h-[44px]"
                              data-testid={`edit-mobile-${it.email}`}
                            >
                              <Pencil size={14} /> {t("admin.whitelist_edit_credits")}
                            </button>
                            <button
                              onClick={() => setAddCreditsFor(it)}
                              className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 text-sm rounded-lg bg-amber-500/15 active:bg-amber-500/30 text-amber-300 border border-amber-500/40 font-medium min-h-[44px]"
                              data-testid={`addcredits-mobile-${it.email}`}
                            >
                              <PlusCircle size={14} /> {t("admin.whitelist_add_credits")}
                            </button>
                            {it.status === "active" ? (
                              <button
                                onClick={() => handleRevoke(it.email)}
                                className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 text-sm rounded-lg bg-red-500/15 active:bg-red-500/30 text-red-300 border border-red-500/40 font-medium min-h-[44px]"
                                data-testid={`revoke-mobile-${it.email}`}
                              >
                                <Ban size={14} /> {t("admin.whitelist_revoke")}
                              </button>
                            ) : (
                              <button
                                onClick={() => handleReactivate(it.email)}
                                className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 text-sm rounded-lg bg-emerald-500/15 active:bg-emerald-500/30 text-emerald-300 border border-emerald-500/40 font-medium min-h-[44px]"
                                data-testid={`reactivate-mobile-${it.email}`}
                              >
                                <Check size={14} /> {t("admin.whitelist_reactivate")}
                              </button>
                            )}
                            <button
                              onClick={() => handleDelete(it.email)}
                              className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 text-sm rounded-lg bg-red-500/10 active:bg-red-500/25 text-red-300 border border-red-500/30 font-medium min-h-[44px]"
                              data-testid={`delete-mobile-${it.email}`}
                            >
                              <Trash2 size={14} /> {t("admin.whitelist_delete")}
                            </button>
                            <label className="col-span-2 text-[10px] tiny-caps text-[color:var(--fg-muted)] mt-1 block">
                              {t("admin.move_to", { defaultValue: "Sposta in..." })}
                            </label>
                            <select
                              value={(it.tag || "").toLowerCase()}
                              onChange={(e) => handleMoveTag(it.email, e.target.value)}
                              className="col-span-2 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm min-h-[44px]"
                              data-testid={`move-select-mobile-${it.email}`}
                            >
                              <option value="">{t("admin.tab_whitelist", { defaultValue: "Whitelist" })}</option>
                              <option value="free_trial">{t("admin.tab_free_trial", { defaultValue: "Free Trial" })}</option>
                              <option value="reseller">{t("admin.tab_resellers", { defaultValue: "Resellers" })}</option>
                              <option value="subscriber">{t("admin.tab_subscribers", { defaultValue: "Subscribers" })}</option>
                            </select>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Desktop table */}
              <div className="hidden md:block rounded-xl border border-white/10 overflow-visible">
              <table className="w-full text-sm">
                <thead className="bg-white/5 text-[10px] tiny-caps text-[color:var(--fg-muted)]">
                  <tr>
                    <th className="text-left px-4 py-2">{t("admin.whitelist_name", { defaultValue: "Nome" })}</th>
                    <th className="text-left px-4 py-2">{t("admin.whitelist_email")}</th>
                    <th className="text-left px-4 py-2">{t("admin.whitelist_join_date", { defaultValue: "Iscritto il" })}</th>
                    <th className="text-left px-4 py-2">{t("admin.whitelist_usage")}</th>
                    <th className="text-left px-4 py-2">{t("admin.whitelist_plan", { defaultValue: "Piano" })}</th>
                    <th className="text-left px-4 py-2">{t("admin.whitelist_credits_breakdown", { defaultValue: "Mensili / Rollover" })}</th>
                    <th className="text-left px-4 py-2">{t("admin.whitelist_status")}</th>
                    <th className="text-right px-4 py-2">{t("admin.whitelist_actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredWhitelist.map((it) => {
                    const isEditing = editingEmail === it.email;
                    return (
                      <tr key={it.email} className="border-t border-white/5" data-testid={`wl-row-${it.email}`}>
                        <td className="px-4 py-3 text-white text-xs">
                          {it.name || <span className="text-[color:var(--fg-muted)] italic">·</span>}
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-white text-xs flex items-center gap-1.5">
                            {it.email}
                            {it.tag === "free_trial" && (
                              <span
                                className="text-[9px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/40 flex-shrink-0 tracking-wide"
                                title={t("admin.free_trial_hint")}
                              >
                                FREE TRIAL
                              </span>
                            )}
                          </div>
                          {it.notes && (
                            <div className="text-[11px] text-[color:var(--fg-muted)]">{it.notes}</div>
                          )}
                          {Array.isArray(it.credit_notes) && it.credit_notes.length > 0 && (
                            <div className="mt-1.5 space-y-1" data-testid={`credit-notes-${it.email}`}>
                              {it.credit_notes.slice().reverse().map((n) => (
                                <div
                                  key={n.id}
                                  className="inline-flex items-center gap-1.5 pl-2 pr-1 py-0.5 rounded bg-amber-500/10 border border-amber-500/25 text-[10px] text-amber-200"
                                  data-testid={`credit-note-${n.id}`}
                                >
                                  <PlusCircle size={9} className="text-amber-300" />
                                  <span className="tabular-nums font-semibold">+{n.delta}</span>
                                  {n.note && <span className="text-amber-100/80 italic">· {n.note}</span>}
                                  <span className="text-amber-100/50">· {new Date(n.created_at).toLocaleDateString()}</span>
                                  <button
                                    onClick={() => handleDeleteCreditNote(it.email, n.id)}
                                    className="ml-0.5 p-0.5 rounded hover:bg-red-500/25 text-amber-100/60 hover:text-red-300"
                                    title={t("admin.credit_note_delete", { defaultValue: "Rimuovi promemoria" })}
                                    data-testid={`credit-note-delete-${n.id}`}
                                  >
                                    <X size={10} />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-xs text-[color:var(--fg-secondary)] tabular-nums whitespace-nowrap">
                          {it.created_at
                            ? new Date(it.created_at).toLocaleDateString()
                            : <span className="text-[color:var(--fg-muted)]">·</span>}
                        </td>
                        <td className="px-4 py-3 text-xs">
                          {isEditing ? (
                            <input
                              type="number"
                              min={0}
                              max={99999}
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              className="w-20 bg-white/10 border border-white/20 rounded px-2 py-1 text-white text-xs"
                              data-testid={`edit-input-${it.email}`}
                            />
                          ) : (
                            <span>
                              <strong className="text-white">{it.credits_used}</strong>
                              <span className="text-[color:var(--fg-muted)]"> / {it.credit_limit === 99999 ? "∞" : it.credit_limit}</span>
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-xs">
                          <span
                            className="text-[10px] px-2 py-0.5 rounded-full border bg-white/5 border-white/15 text-white uppercase tracking-wider"
                            data-testid={`wl-plan-${it.email}`}
                          >
                            {it.plan || "free"}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-xs tabular-nums">
                          <span className="text-white" data-testid={`wl-credits-${it.email}`}>
                            {it.monthly_credits ?? 0}
                          </span>
                          <span className="text-[color:var(--fg-muted)]"> + {it.accumulated_credits ?? 0}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`text-[10px] px-2 py-0.5 rounded-full border ${
                              it.status === "active"
                                ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/30"
                                : "bg-red-500/10 text-red-300 border-red-500/30"
                            }`}
                          >
                            {it.status === "active"
                              ? t("admin.whitelist_status_active")
                              : t("admin.whitelist_status_revoked")}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="inline-flex gap-1.5">
                            {isEditing ? (
                              <>
                                <button
                                  onClick={() => handlePatchCredits(it.email, parseInt(editValue, 10) || 0)}
                                  className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-md bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-300 border border-emerald-500/30"
                                  data-testid={`save-${it.email}`}
                                >
                                  <Save size={12} />
                                </button>
                                <button
                                  onClick={() => setEditingEmail(null)}
                                  className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-md bg-white/5 hover:bg-white/10 text-[color:var(--fg-secondary)] border border-white/10"
                                >
                                  <X size={12} />
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  onClick={() => {
                                    setEditingEmail(it.email);
                                    setEditValue(String(it.credit_limit));
                                  }}
                                  className="p-1.5 rounded-md hover:bg-white/5 text-[color:var(--fg-secondary)]"
                                  title={t("admin.whitelist_edit_credits")}
                                  data-testid={`edit-${it.email}`}
                                >
                                  <Pencil size={12} />
                                </button>
                                <button
                                  onClick={() => setAddCreditsFor(it)}
                                  className="p-1.5 rounded-md hover:bg-white/5 text-amber-300"
                                  title={t("admin.whitelist_add_credits")}
                                  data-testid={`addcredits-${it.email}`}
                                >
                                  <PlusCircle size={12} />
                                </button>
                                {it.status === "active" ? (
                                  <button
                                    onClick={() => handleRevoke(it.email)}
                                    className="p-1.5 rounded-md hover:bg-red-500/10 text-red-300"
                                    title={t("admin.whitelist_revoke")}
                                    data-testid={`revoke-${it.email}`}
                                  >
                                    <Ban size={12} />
                                  </button>
                                ) : (
                                  <button
                                    onClick={() => handleReactivate(it.email)}
                                    className="p-1.5 rounded-md hover:bg-emerald-500/10 text-emerald-300"
                                    title={t("admin.whitelist_reactivate")}
                                    data-testid={`reactivate-${it.email}`}
                                  >
                                    <Check size={12} />
                                  </button>
                                )}
                                <div className="relative inline-block">
                                  <button
                                    onClick={() =>
                                      setMoveMenuFor(
                                        moveMenuFor === it.email ? null : it.email
                                      )
                                    }
                                    className="p-1.5 rounded-md hover:bg-white/5 text-blue-300"
                                    title={t("admin.move_to", { defaultValue: "Sposta in..." })}
                                    data-testid={`move-${it.email}`}
                                  >
                                    <MoveRight size={12} />
                                  </button>
                                  {moveMenuFor === it.email && (
                                    <div
                                      className="absolute right-0 bottom-full mb-1 z-50 min-w-[180px] rounded-lg border border-white/10 bg-[color:var(--bg-elev)] shadow-2xl py-1"
                                      data-testid={`move-menu-${it.email}`}
                                    >
                                      {[
                                        { tag: "", label: t("admin.tab_whitelist", { defaultValue: "Whitelist" }) },
                                        { tag: "free_trial", label: t("admin.tab_free_trial", { defaultValue: "Free Trial" }) },
                                        { tag: "reseller", label: t("admin.tab_resellers", { defaultValue: "Resellers" }) },
                                        { tag: "subscriber", label: t("admin.tab_subscribers", { defaultValue: "Subscribers" }) },
                                      ].map((opt) => {
                                        const current = (it.tag || "").toLowerCase();
                                        const isCurrent = current === opt.tag;
                                        return (
                                          <button
                                            key={opt.tag || "none"}
                                            onClick={() => !isCurrent && handleMoveTag(it.email, opt.tag)}
                                            disabled={isCurrent}
                                            className={`w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 ${
                                              isCurrent
                                                ? "text-[color:var(--fg-muted)] cursor-default"
                                                : "text-white hover:bg-white/5"
                                            }`}
                                            data-testid={`move-to-${opt.tag || "whitelist"}-${it.email}`}
                                          >
                                            {isCurrent && <Check size={10} className="text-emerald-400" />}
                                            <span className={isCurrent ? "ml-0" : "ml-3"}>{opt.label}</span>
                                          </button>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                                <button
                                  onClick={() => handleDelete(it.email)}
                                  className="p-1.5 rounded-md hover:bg-red-500/10 text-red-300"
                                  title={t("admin.whitelist_delete")}
                                  data-testid={`delete-${it.email}`}
                                >
                                  <Trash2 size={12} />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            </>
          )}
        </section>
        )}

        {/* Manual refund · activeTab === "refunds" */}
        {activeTab === "refunds" && (
        <section data-testid="section-manual-refund">
          <h2 className="text-base font-bold text-white mb-3 flex items-center gap-2">
            <Undo2 size={16} /> {t("admin.refund_title")}
          </h2>
          <p className="text-xs text-[color:var(--fg-muted)] mb-3 max-w-2xl">
            {t("admin.refund_desc")}
          </p>
          <div
            className="rounded-xl border border-white/10 bg-white/[0.02] p-4 flex flex-col md:flex-row gap-3 md:items-end mb-4"
            data-testid="refund-form"
          >
            <div className="flex-1 min-w-0">
              <label className="tiny-caps mb-1 block text-[10px]">
                {t("admin.refund_email_label")}
              </label>
              <input
                type="email"
                value={refundEmail}
                onChange={(e) => setRefundEmail(e.target.value)}
                placeholder="user@example.com"
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
                data-testid="refund-email-input"
                disabled={refunding}
              />
            </div>
            <div className="w-full md:w-24">
              <label className="tiny-caps mb-1 block text-[10px]">
                {t("admin.refund_amount_label")}
              </label>
              <input
                type="number"
                min={1}
                max={50}
                value={refundAmount}
                onChange={(e) => setRefundAmount(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm tabular-nums"
                data-testid="refund-amount-input"
                disabled={refunding}
              />
            </div>
            <div className="flex-1 min-w-0">
              <label className="tiny-caps mb-1 block text-[10px]">
                {t("admin.refund_reason_label")}
              </label>
              <input
                type="text"
                value={refundReason}
                onChange={(e) => setRefundReason(e.target.value)}
                placeholder={t("admin.refund_reason_placeholder")}
                maxLength={500}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
                data-testid="refund-reason-input"
                disabled={refunding}
              />
            </div>
            <button
              type="button"
              onClick={submitManualRefund}
              disabled={refunding}
              className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-200 text-sm border border-emerald-500/30 disabled:opacity-50 shrink-0"
              data-testid="refund-submit-btn"
            >
              <Undo2 size={14} /> {refunding ? t("admin.refund_submitting") : t("admin.refund_submit")}
            </button>
          </div>
          {refundList.length === 0 ? (
            <p className="text-xs text-[color:var(--fg-muted)] italic">
              {t("admin.refund_history_empty")}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table
                className="w-full text-xs min-w-[600px]"
                data-testid="refund-history-table"
              >
                <thead>
                  <tr className="text-left text-[color:var(--fg-muted)] tiny-caps">
                    <th className="px-2 py-2">{t("admin.refund_col_when")}</th>
                    <th className="px-2 py-2">{t("admin.refund_col_email")}</th>
                    <th className="px-2 py-2 text-center">{t("admin.refund_col_amount")}</th>
                    <th className="px-2 py-2">{t("admin.refund_col_reason")}</th>
                    <th className="px-2 py-2">{t("admin.refund_col_by")}</th>
                    <th className="px-2 py-2 text-right">{t("admin.whitelist_actions", { defaultValue: "Azioni" })}</th>
                  </tr>
                </thead>
                <tbody>
                  {refundList.map((r) => (
                    <tr
                      key={r.id}
                      className="border-t border-white/5"
                      data-testid={`refund-row-${r.id}`}
                    >
                      <td className="px-2 py-2 tabular-nums text-[color:var(--fg-muted)]">
                        {new Date(r.created_at).toLocaleString()}
                      </td>
                      <td className="px-2 py-2 text-white break-all">{r.email}</td>
                      <td className="px-2 py-2 text-center metric-value text-emerald-300">
                        +{r.amount}
                      </td>
                      <td className="px-2 py-2 text-[color:var(--fg-secondary)]">
                        {(r.reason || "").replace(/^manual:/, "")}
                      </td>
                      <td className="px-2 py-2 text-[color:var(--fg-muted)] break-all">
                        {r.refunded_by || "·"}
                      </td>
                      <td className="px-2 py-2 text-right">
                        <button
                          onClick={() => handleDeleteManualRefund(r.id)}
                          className="p-1.5 rounded-md hover:bg-red-500/10 text-red-300"
                          title={t("admin.refund_delete", { defaultValue: "Rimuovi voce" })}
                          data-testid={`refund-delete-${r.id}`}
                        >
                          <Trash2 size={12} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
        )}

        {/* Audit log · dedicated Log tab, grouped by day, with delete + clear all */}
        {activeTab === "log" && (
        <section data-testid="section-audit">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <h2 className="text-base font-bold text-white flex items-center gap-2">
              <ScrollText size={16} /> {t("admin.audit_title")}
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 text-[color:var(--fg-muted)] border border-white/10">
                {audit.length}
              </span>
            </h2>
            {audit.length > 0 && (
              <button
                onClick={handleClearAuditLog}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-200 text-xs border border-red-500/30"
                data-testid="audit-clear-all"
              >
                <Trash2 size={12} /> {t("admin.audit_clear_all", { defaultValue: "Cancella tutto" })}
              </button>
            )}
          </div>
          {audit.length === 0 ? (
            <p className="text-xs text-[color:var(--fg-muted)] italic">{t("admin.audit_empty")}</p>
          ) : (
            <div className="space-y-4">
              {(() => {
                // Group audit entries by date (YYYY-MM-DD), most recent day first.
                const groups = {};
                audit.forEach((a) => {
                  const key = (a.timestamp || "").slice(0, 10);
                  if (!groups[key]) groups[key] = [];
                  groups[key].push(a);
                });
                const days = Object.keys(groups).sort().reverse();
                return days.map((day, dIdx) => (
                  <details
                    key={day}
                    open={dIdx === 0}
                    className="rounded-lg border border-white/10 bg-white/[0.02]"
                    data-testid={`audit-day-${day}`}
                  >
                    <summary className="cursor-pointer px-3 py-2 text-xs text-[color:var(--fg-secondary)] tiny-caps flex items-center gap-2 select-none">
                      {new Date(day).toLocaleDateString(undefined, { weekday: "short", year: "numeric", month: "short", day: "numeric" })}
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-white/5 text-[color:var(--fg-muted)] border border-white/10">
                        {groups[day].length}
                      </span>
                      <button
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleDeleteAuditDay(day); }}
                        className="ml-auto p-1 rounded hover:bg-red-500/15 text-[color:var(--fg-muted)] hover:text-red-300 opacity-60 hover:opacity-100"
                        title={t("admin.audit_delete_day", { defaultValue: "Cancella intero giorno" })}
                        data-testid={`audit-delete-day-${day}`}
                      >
                        <Trash2 size={12} />
                      </button>
                    </summary>
                    <ol className="space-y-1.5 text-xs px-3 pb-3 pt-1">
                      {groups[day].map((a, idx) => (
                        <li
                          key={a.id || `${a.timestamp}-${idx}`}
                          className="flex flex-wrap items-baseline gap-x-2 px-3 py-2 rounded-md bg-white/[0.02] border border-white/5 group"
                          data-testid={`audit-${a.id || idx}`}
                        >
                          <span className="text-[color:var(--fg-muted)] tabular-nums text-[11px]">
                            {new Date(a.timestamp).toLocaleTimeString()}
                          </span>
                          <span className="text-white">{a.admin_email.split("@")[0]}</span>
                          <span className="text-[color:var(--fg-secondary)]">
                            {t(`admin.audit_actions.${a.action}`, a.action)}
                          </span>
                          <span className="text-white">{a.target_email}</span>
                          {a.new_value?.credit_limit != null && (
                            <span className="text-[color:var(--fg-muted)]">
                              · {a.new_value.credit_limit} {t("admin.credits_suffix")}
                            </span>
                          )}
                          {a.id && (
                            <button
                              onClick={() => handleDeleteAuditEntry(a.id)}
                              className="ml-auto p-1 rounded hover:bg-red-500/15 text-[color:var(--fg-muted)] hover:text-red-300 opacity-60 hover:opacity-100"
                              title={t("admin.audit_delete", { defaultValue: "Rimuovi voce" })}
                              data-testid={`audit-delete-${a.id}`}
                            >
                              <Trash2 size={11} />
                            </button>
                          )}
                        </li>
                      ))}
                    </ol>
                  </details>
                ));
              })()}
            </div>
          )}
        </section>
        )}
        {/* Waitlist · activeTab === "waitlist" */}
        {activeTab === "waitlist" && (
        <section className="mt-10 mb-10" data-testid="section-waitlist">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h2 className="text-base font-bold text-white flex items-center gap-2">
              <Mail size={16} className="text-fuchsia-300" />
              {t("admin_waitlist.title")}
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-fuchsia-500/15 text-fuchsia-200 border border-fuchsia-500/30">
                {waitlistTotal}
              </span>
            </h2>
            <button
              onClick={downloadWaitlistCsv}
              disabled={waitlist.length === 0}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-fuchsia-500/15 hover:bg-fuchsia-500/25 text-fuchsia-100 text-xs border border-fuchsia-500/30 disabled:opacity-50"
              data-testid="waitlist-export-csv"
            >
              <Download size={12} /> {t("admin_waitlist.export_csv")}
            </button>
          </div>
          {/* Search bar for waitlist */}
          <div className="mb-3 relative">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--fg-muted)] pointer-events-none" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t("admin.search_placeholder", { defaultValue: "Cerca per nome o email…" })}
              className="w-full bg-white/5 border border-white/10 rounded-lg pl-8 pr-3 py-2 text-white text-sm placeholder:text-[color:var(--fg-muted)]"
              data-testid="admin-search-input-waitlist"
            />
          </div>
          {waitlist.length === 0 ? (
            <p className="text-xs text-[color:var(--fg-muted)] italic">
              {t("admin_waitlist.empty")}
            </p>
          ) : (
            <div className="rounded-xl border border-white/10 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[520px]">
                  <thead className="bg-white/5 text-[10px] tiny-caps text-[color:var(--fg-muted)]">
                    <tr>
                      <th className="text-left px-4 py-2">
                        {t("admin_waitlist.col_email")}
                      </th>
                      <th className="text-left px-4 py-2">
                        {t("admin_waitlist.col_date")}
                      </th>
                      <th className="text-left px-4 py-2">
                        {t("admin_waitlist.col_tag")}
                      </th>
                      <th className="text-left px-4 py-2">
                        {t("admin_waitlist.col_flags")}
                      </th>
                      <th className="text-right px-4 py-2">
                        {t("admin_waitlist.col_actions")}
                      </th>
                    </tr>
                  </thead>
                  <tbody data-testid="waitlist-table">
                    {filteredWaitlist.map((w) => (
                      <tr
                        key={w.email}
                        className="border-t border-white/5"
                        data-testid={`waitlist-row-${w.email}`}
                      >
                        <td className="px-4 py-2 text-white break-all">
                          {w.email}
                          {w.aliases && w.aliases.length > 1 && (
                            <div className="text-[10px] text-[color:var(--fg-muted)] mt-0.5">
                              {t("admin_waitlist.aliases_hint", {
                                count: w.aliases.length,
                              })}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-2 text-[color:var(--fg-secondary)] tabular-nums text-xs">
                          {w.created_at
                            ? new Date(w.created_at).toLocaleString()
                            : "·"}
                        </td>
                        <td className="px-4 py-2">
                          <span className="inline-block px-2 py-0.5 rounded-md bg-fuchsia-500/10 text-fuchsia-200 text-[10px] border border-fuchsia-500/30">
                            {w.tag || "10% launch discount"}
                          </span>
                        </td>
                        <td className="px-4 py-2">
                          {w.flags && w.flags.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {w.flags.map((f) => (
                                <span
                                  key={f}
                                  title={t(`admin_waitlist.flag_${f}_hint`, {
                                    defaultValue: f,
                                  })}
                                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-red-500/10 text-red-300 text-[10px] border border-red-500/30 cursor-help"
                                  data-testid={`waitlist-flag-${f}`}
                                >
                                  🚩 {t(`admin_waitlist.flag_${f}`, { defaultValue: f })}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-[10px] text-emerald-400/70">
                              {t("admin_waitlist.flag_ok")}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-right">
                          <button
                            onClick={() => handleDeleteWaitlist(w.email)}
                            title={t("admin_waitlist.delete_title")}
                            className="p-1.5 rounded-md hover:bg-red-500/10 text-red-300"
                            data-testid={`waitlist-delete-${w.email}`}
                          >
                            <Trash2 size={12} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </section>
        )}
      </main>

      {showAddModal && (
        <AddUserModal
          onClose={() => setShowAddModal(false)}
          onSave={handleAdd}
        />
      )}
      {addCreditsFor && (
        <AddCreditsModal
          user={addCreditsFor}
          onClose={() => setAddCreditsFor(null)}
          onSave={(delta, note) => handleAddCredits(addCreditsFor.email, delta, note)}
        />
      )}
    </div>
  );
}

function AddCreditsModal({ user, onClose, onSave }) {
  const { t } = useTranslation();
  const [delta, setDelta] = useState(10);
  const [note, setNote] = useState("");
  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="add-credits-modal"
    >
      <div
        className="max-w-md w-full rounded-2xl border border-white/10 bg-[color:var(--bg-elev)] p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-lg font-bold text-white mb-4">
          {t("admin.whitelist_add_credits_title", { email: user.email })}
        </h3>
        <div className="text-xs text-[color:var(--fg-secondary)] mb-4">
          {user.credits_used} / {user.credit_limit === 99999 ? "∞" : user.credit_limit} →{" "}
          <strong className="text-emerald-300">
            {user.credits_used} / {user.credit_limit + (parseInt(delta, 10) || 0)}
          </strong>
        </div>
        <label className="block text-xs text-[color:var(--fg-muted)] mb-1">
          {t("admin.whitelist_add_credits_delta")}
        </label>
        <input
          type="number"
          min={1}
          max={99999}
          value={delta}
          onChange={(e) => setDelta(parseInt(e.target.value, 10) || 0)}
          className="w-full mb-3 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
          data-testid="addcredits-delta-input"
          autoFocus
        />
        <label className="block text-xs text-[color:var(--fg-muted)] mb-1">
          {t("admin.whitelist_add_credits_note")}
        </label>
        <input
          type="text"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          className="w-full mb-4 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
          data-testid="addcredits-note-input"
        />
        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-3 py-2 rounded-lg text-xs text-[color:var(--fg-secondary)] hover:text-white"
            data-testid="addcredits-cancel-btn"
          >
            {t("admin.whitelist_cancel")}
          </button>
          <button
            onClick={() => (parseInt(delta, 10) || 0) > 0 && onSave(parseInt(delta, 10), note.trim())}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs bg-amber-500/20 hover:bg-amber-500/30 text-amber-200 border border-amber-500/30"
            data-testid="addcredits-save-btn"
          >
            <PlusCircle size={12} /> {t("admin.whitelist_add_credits")}
          </button>
        </div>
      </div>
    </div>
  );
}

function AddUserModal({ onClose, onSave }) {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [credits, setCredits] = useState(10);
  const [notes, setNotes] = useState("");
  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="add-user-modal"
    >
      <div
        className="max-w-md w-full rounded-2xl border border-white/10 bg-[color:var(--bg-elev)] p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-lg font-bold text-white mb-4">
          {t("admin.whitelist_add_title")}
        </h3>
        <label className="block text-xs text-[color:var(--fg-muted)] mb-1">
          {t("admin.whitelist_email")}
        </label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full mb-3 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
          placeholder="utente@example.com"
          data-testid="add-email-input"
          autoFocus
        />
        <label className="block text-xs text-[color:var(--fg-muted)] mb-1">
          {t("admin.whitelist_credits")}
        </label>
        <input
          type="number"
          min={0}
          max={99999}
          value={credits}
          onChange={(e) => setCredits(parseInt(e.target.value, 10) || 0)}
          className="w-full mb-3 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
          data-testid="add-credits-input"
        />
        <label className="block text-xs text-[color:var(--fg-muted)] mb-1">
          {t("admin.whitelist_notes")}
        </label>
        <input
          type="text"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className="w-full mb-4 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
          data-testid="add-notes-input"
        />
        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-3 py-2 rounded-lg text-xs text-[color:var(--fg-secondary)] hover:text-white"
            data-testid="add-cancel-btn"
          >
            {t("admin.whitelist_cancel")}
          </button>
          <button
            onClick={() => email.trim() && onSave(email.trim().toLowerCase(), credits, notes.trim())}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-200 border border-emerald-500/30"
            data-testid="add-save-btn"
          >
            <Save size={12} /> {t("admin.whitelist_save")}
          </button>
        </div>
      </div>
    </div>
  );
}
